import { AxiosInstance } from "../../axios/axiosInstance";
import { apiRoutes } from "../../utils/ApiRoutes";
import {
  EmbedKind,
  IEmailSchedulePayload,
  IRankCompareParams,
  IReportListParams,
  IReportUpdatePayload,
  ISendNowPayload,
} from "../../components/ReportEditor/apiTypes";

// Public calls skip the auth header so a 401 or 404 never touches the login flow
const PUBLIC_CONFIG = { headers: { skipAuth: true } };

export const getEligibleBusinessesApi = async (params?: {
  search?: string;
  page?: number;
  limit?: number;
}) => AxiosInstance.get(apiRoutes.reportBuilderEligible, { params });

export const syncRankBusinessesApi = async () =>
  AxiosInstance.post(apiRoutes.reportBuilderSyncRankBusinesses);

export const createReportApi = async (payload: { businessId: string }) =>
  AxiosInstance.post(apiRoutes.reportBuilder, payload);

export const listReportsApi = async (params?: IReportListParams) =>
  AxiosInstance.get(apiRoutes.reportBuilder, { params });

export const getReportApi = async (id: string) =>
  AxiosInstance.get(apiRoutes.reportBuilderById(id));

export const updateReportApi = async (
  id: string,
  payload: IReportUpdatePayload,
) => AxiosInstance.patch(apiRoutes.reportBuilderById(id), payload);

export const deleteReportApi = async (id: string) =>
  AxiosInstance.delete(apiRoutes.reportBuilderById(id));

export const bulkDeleteReportsApi = async (payload: { ids: string[] }) =>
  AxiosInstance.post(apiRoutes.reportBuilderBulkDelete, payload);

export const getReportDataApi = async (
  id: string,
  sections?: string[],
  fresh?: boolean,
  preview?: { period?: string; compare?: string },
) =>
  AxiosInstance.get(apiRoutes.reportBuilderData(id), {
    params:
      sections?.length || fresh || preview?.period || preview?.compare
        ? {
            ...(sections?.length ? { sections: sections.join(",") } : {}),
            ...(fresh ? { fresh: true } : {}),
            // Unsaved period and compare, so the data follows the selectors before Save
            ...(preview?.period ? { period: preview.period } : {}),
            ...(preview?.compare ? { compare: preview.compare } : {}),
          }
        : undefined,
  });

export const getRankScansApi = async (id: string, heatmapId: string) =>
  AxiosInstance.get(apiRoutes.reportBuilderRankScans(id), {
    params: { heatmapId },
  });

export const getRankCompareApi = async (
  id: string,
  params: IRankCompareParams,
) => AxiosInstance.get(apiRoutes.reportBuilderRankCompare(id), { params });

export const updateEmailScheduleApi = async (
  id: string,
  payload: IEmailSchedulePayload,
) => AxiosInstance.put(apiRoutes.reportBuilderEmailSchedule(id), payload);

export const getReportAssigneesApi = async (id: string) =>
  AxiosInstance.get(apiRoutes.reportBuilderAssignees(id));

export const embedMetaApi = async (
  id: string,
  payload: { url: string; kind: EmbedKind },
) => AxiosInstance.post(apiRoutes.reportBuilderEmbedMeta(id), payload);

export const sendReportNowApi = async (id: string, payload: ISendNowPayload) =>
  AxiosInstance.post(apiRoutes.reportBuilderSend(id), payload);

export const bulkScheduleReportsApi = async (
  payload: IEmailSchedulePayload & { ids: string[] },
) => AxiosInstance.post(apiRoutes.reportBuilderBulkSchedule, payload);

export const bulkSendReportsApi = async (payload: { ids: string[] }) =>
  AxiosInstance.post(apiRoutes.reportBuilderBulkSend, payload);

export const getAiSummaryApi = async () =>
  AxiosInstance.get(apiRoutes.reportBuilderAiSummary);

export const refreshAiSummaryApi = async () =>
  AxiosInstance.post(apiRoutes.reportBuilderAiSummaryRefresh);

export const getPublicReportApi = async (id: string) =>
  AxiosInstance.get(apiRoutes.publicReportBuilder(id), PUBLIC_CONFIG);

export const getPublicRankCompareApi = async (
  id: string,
  params: IRankCompareParams,
) =>
  AxiosInstance.get(apiRoutes.publicReportBuilderRankCompare(id), {
    ...PUBLIC_CONFIG,
    params,
  });
